/* eslint-disable jsx-a11y/accessible-emoji */
import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {fetchAsync} from '../../services/http/http';
import {httpToken} from '../../services/http/httpBase';
import {getCompany} from '../../store/data/company/companySelectors';
import {RequestInviteButtonV3} from './requestInviteButtonV3';
import {CallToActionTypesEnum} from './visitorCommon';

/*
 * Props.
 */

interface VisitorViewerSidebarContentCtaProps {
  inviterName?: string;
  cta: CallToActionTypesEnum;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  display: grid;
  grid-auto-rows: auto;
  grid-row-gap: 12px;
  justify-items: center;
  position: relative;
  background-color: ${(p) => p.theme.greys.white};
  border-radius: 16px;
  margin: 24px 24px 0px;
  width: 272px;
  padding: 16px;
`;

const StyledTitleDiv = styled.div`
  font-weight: ${fontWeights.bold};
  text-align: center;
  font-size: 18px;
`;

const StyledDescriptionDiv = styled.div`
  text-align: center;
  font-size: 14px;
  line-height: 21px;
  max-width: 238px;
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledSubdescriptionDiv = styled.div`
  font-size: 12px;
  line-height: 12px;
  text-align: center;

  a {
    color: #367fee;
    font-weight: ${fontWeights.bold};
  }
`;

const StyledSuccessTextDiv = styled.div`
  position: absolute;
  bottom: -8px;
  transform: translateY(100%);
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  max-width: 210px;
  line-height: 17px;
  text-align: center;
  color: ${(p) => p.theme.greys.white};
`;

const StyledButtonDiv = styled.div`
  margin-top: 12px;
  border-radius: 14px;
  overflow: hidden;
  ${(p) => boxShadow(p.theme, `0px 3px 14px rgba(26, 76, 168, 0.2)`)};
  & [role='button'] > div {
    transition: background-color 250ms ease-out;
  }
  margin-top: 4px;
`;

const StyledButtonContentDiv = styled.div`
  padding: 0px 40px;
`;

/*
 * Component.
 */

export const VisitorViewerSidebarContentCta: FC<VisitorViewerSidebarContentCtaProps> = ({
  inviterName,
  cta,
}) => {
  const [requestSent, setRequestSent] = useState(false);
  const company = useAppSelector(getCompany);
  const companyName = company.name;

  const requestAnInvite = () => {
    setRequestSent(true);
    fetchAsync(`/visitors/${httpToken()}/request-invite`);
  };

  switch (cta) {
    case CallToActionTypesEnum.REQUEST_INVITE:
      return (
        <StyledContentDiv>
          <StyledTitleDiv>
            <Trans id="visitor-sidebar-title-v3">Join {companyName} on Front</Trans>
          </StyledTitleDiv>
          <StyledDescriptionDiv>
            <Trans id="visitor-sidebar-description-v3">
              Do you want your company admins to make you a full Front user?
            </Trans>
          </StyledDescriptionDiv>
          <RequestInviteButtonV3 wasInviteRequested={requestSent} onClick={requestAnInvite} />
          <StyledSubdescriptionDiv>
            <Trans id="visitor-sidebar-signin-link-v3">
              Or,{' '}
              <a href="https://app.frontapp.com/signin" target="_blank" rel="noreferrer">
                sign in to your account
              </a>
            </Trans>
          </StyledSubdescriptionDiv>
          {requestSent && (
            <StyledSuccessTextDiv>
              <Trans id="visitor-sidebar-request-sent-v3">
                An admin at your company will send an invite to your inbox soon 🙌
              </Trans>
            </StyledSuccessTextDiv>
          )}
        </StyledContentDiv>
      );
    case CallToActionTypesEnum.TRY_FRONT:
    default:
      return (
        <StyledContentDiv>
          <StyledTitleDiv>
            <Trans id="visitor-sidebar-title-tryfront-v3">Get started with Front</Trans>
          </StyledTitleDiv>
          <StyledDescriptionDiv>
            <Trans id="visitor-sidebar-description-tryfront-v3">
              Access more features in Front by signing up for a free account!
            </Trans>
          </StyledDescriptionDiv>
          <StyledButtonDiv>
            <Button
              onClick={onTryFrontClick}
              size={VisualSizesEnum.LARGE}
              variant={ButtonVariantsEnum.PRIMARY}
            >
              <StyledButtonContentDiv>
                <Trans id="visitor-sidebar-button-v3">Sign up for free</Trans>
              </StyledButtonContentDiv>
            </Button>
          </StyledButtonDiv>
          <StyledSubdescriptionDiv>
            <Trans id="visitor-sidebar-signin-link-tryfront-v3">
              Or,{' '}
              <a href="https://app.frontapp.com/signin" target="_blank" rel="noreferrer">
                sign in to your account
              </a>
            </Trans>
          </StyledSubdescriptionDiv>
        </StyledContentDiv>
      );
  }
};

/*
 * Event handlers.
 */

function onTryFrontClick() {
  openInBrowser('https://frontapp.com/signup?utm_source=front&utm_campaign=share_thread_p3');
}
